import { useEffect, useMemo, useState } from 'react';
import { EMPTY_FILTERS } from '../components/courses/courseListConstants';

export { SORT_OPTIONS, RATING_FILTER_OPTIONS, EMPTY_FILTERS } from '../components/courses/courseListConstants';

function findDefaultClassId(classes = []) {
  if (!classes.length) return '';

  const class11 = classes.find((item) => {
    const label = String(item.label || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    return label.includes('class 11') || /(^|\s)11(\s|$)/.test(label);
  });

  return String(class11?.id || classes[0].id);
}

function filtersWithDefaultClass(classes) {
  const classId = findDefaultClassId(classes);
  return {
    ...EMPTY_FILTERS,
    classes: classId ? [classId] : [],
  };
}

export function useCourseListFilters(courses, { defaultSort = 'desc' } = {}) {
  const [sort, setSort] = useState(defaultSort);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [hasAppliedDefaultClass, setHasAppliedDefaultClass] = useState(false);

  const filterOptions = useMemo(() => {
    const classesMap = new Map();
    const subjectsMap = new Map();
    const subjectClassMap = new Map();
    const instructorsMap = new Map();

    (courses || []).forEach((course) => {
      if (course?.categoryId && course?.classLabel) {
        classesMap.set(String(course.categoryId), course.classLabel);
      }
      if (course?.subjectId && course?.subject) {
        subjectsMap.set(String(course.subjectId), course.subject);
        const subjectId = String(course.subjectId);
        const classId = course?.categoryId ? String(course.categoryId) : null;
        if (!subjectClassMap.has(subjectId)) {
          subjectClassMap.set(subjectId, new Set());
        }
        if (classId) {
          subjectClassMap.get(subjectId).add(classId);
        }
      }
      if (course?.instructorId && course?.instructorName) {
        instructorsMap.set(String(course.instructorId), course.instructorName);
      }
    });

    const classes = Array.from(classesMap.entries()).map(([id, label]) => ({ id, label }));
    const subjects = Array.from(subjectsMap.entries()).map(([id, label]) => ({
      id,
      label,
      classIds: Array.from(subjectClassMap.get(String(id)) || []),
    }));
    const instructors = Array.from(instructorsMap.entries()).map(([id, label]) => ({ id, label }));

    classes.sort((a, b) => a.label.localeCompare(b.label));
    subjects.sort((a, b) => a.label.localeCompare(b.label));
    instructors.sort((a, b) => a.label.localeCompare(b.label));

    return { classes, subjects, instructors };
  }, [courses]);

  useEffect(() => {
    if (hasAppliedDefaultClass || !filterOptions.classes.length) return;

    const defaults = filtersWithDefaultClass(filterOptions.classes);
    setFilters(defaults);
    setDraftFilters(defaults);
    setHasAppliedDefaultClass(true);
  }, [filterOptions.classes, hasAppliedDefaultClass]);

  const applyFilters = () => {
    const classId = draftFilters.classes?.[0] || findDefaultClassId(filterOptions.classes);
    setFilters({
      ...draftFilters,
      classes: classId ? [String(classId)] : [],
    });
    setShowFilters(false);
  };

  const clearFilters = () => {
    const defaults = filtersWithDefaultClass(filterOptions.classes);
    setDraftFilters(defaults);
    setFilters(defaults);
    setShowFilters(false);
  };

  const openFilters = () => {
    const classId = filters.classes?.[0] || findDefaultClassId(filterOptions.classes);
    setDraftFilters({
      ...filters,
      classes: classId ? [String(classId)] : [],
    });
    setShowFilters(true);
    setShowSort(false);
  };

  const filteredCourses = useMemo(() => {
    const search = (filters.search || '').trim().toLowerCase();
    const minRating = filters.rating ? Number(filters.rating) : 0;
    const classSet = new Set((filters.classes || []).map(String));
    const subjectSet = new Set((filters.subjects || []).map(String));
    const instructorSet = new Set((filters.instructors || []).map(String));

    const list = (courses || []).filter((course) => {
      if (!course) return false;
      if (minRating && Number(course.rating || 0) < minRating) return false;
      if (classSet.size && !classSet.has(String(course.categoryId))) return false;
      if (subjectSet.size && !subjectSet.has(String(course.subjectId))) return false;
      if (instructorSet.size && !instructorSet.has(String(course.instructorId))) return false;
      if (search) {
        const hay = `${course.title || ''} ${course.instructorName || ''}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });

    list.sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
    if (sort === 'desc') list.reverse();
    return list;
  }, [courses, filters, sort]);

  const toggleDraftList = (key, id, checked) => {
    setDraftFilters((prev) => {
      const list = new Set((prev[key] || []).map(String));
      if (checked) list.add(String(id));
      else list.delete(String(id));
      return { ...prev, [key]: Array.from(list) };
    });
  };

  const setDraftClass = (classId) => {
    setDraftFilters((prev) => {
      const nextClassId = classId
        ? String(classId)
        : findDefaultClassId(filterOptions.classes);

      const allowedSubjectIds = new Set(
        filterOptions.subjects
          .filter((subject) => subject.classIds.includes(nextClassId))
          .map((subject) => String(subject.id))
      );

      return {
        ...prev,
        classes: nextClassId ? [nextClassId] : [],
        subjects: (prev.subjects || [])
          .map(String)
          .filter((subjectId) => allowedSubjectIds.has(subjectId)),
      };
    });
  };

  return {
    sort,
    setSort,
    filters,
    draftFilters,
    setDraftFilters,
    showFilters,
    setShowFilters,
    showSort,
    setShowSort,
    filterOptions,
    filteredCourses,
    applyFilters,
    clearFilters,
    openFilters,
    toggleDraftList,
    setDraftClass,
  };
}
